import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { getProductBySlug } from '../api'
import { useCart } from '../context/CartContext'
import SocialProofNotification from '../components/notifications/SocialProofNotification'
import './ProductDetail.css'

export default function ProductDetail() {
    const { slug } = useParams()
    const [product, setProduct] = useState(null)
    const [loading, setLoading] = useState(true)
    const [quantity, setQuantity] = useState(1)
    const [selectedImage, setSelectedImage] = useState(0)
    const [addedToCart, setAddedToCart] = useState(false)
    const { addToCart } = useCart()

    useEffect(() => {
        async function fetchProduct() {
            try {
                setLoading(true)

                // Validate slug format
                if (!slug || slug === 'undefined' || slug === 'null') {
                    console.error('Invalid product slug:', slug)
                    setProduct(null)
                    setLoading(false)
                    return
                }

                const data = await getProductBySlug(slug)
                setProduct(data)
            } catch (err) {
                console.error('Error loading product:', {
                    slug,
                    error: err.message,
                    fullError: err
                })
                setProduct(null)
            } finally {
                setLoading(false)
            }
        }
        fetchProduct()
    }, [slug])

    const handleAddToCart = () => {
        if (product) {
            addToCart(product, quantity)
            setAddedToCart(true)
            setTimeout(() => setAddedToCart(false), 2000)
        }
    }

    if (loading) {
        return (
            <div className="product-detail">
                <div className="container">
                    <div className="loading"><div className="spinner"></div></div>
                </div>
            </div>
        )
    }

    if (!product) {
        return (
            <div className="product-detail">
                <div className="container">
                    <div className="not-found">
                        <h2>Product Not Found</h2>
                        <Link to="/products" className="btn btn-primary">Back to Products</Link>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className="product-detail">
            <div className="container">
                {/* Breadcrumb */}
                <nav className="breadcrumb">
                    <Link to="/">Home</Link>
                    <span>/</span>
                    <Link to="/products">Products</Link>
                    <span>/</span>
                    <span>{product.name}</span>
                </nav>

                <div className="product-layout">
                    {/* Gallery */}
                    <div className="product-gallery">
                        <div className="main-image">
                            {product.images?.[selectedImage]?.src ? (
                                <img src={product.images[selectedImage].src} alt={product.name} />
                            ) : (
                                <div className="product-placeholder"><span>📦</span></div>
                            )}
                        </div>
                        {product.images?.length > 1 && (
                            <div className="image-thumbnails">
                                {product.images.map((img, idx) => (
                                    <button
                                        key={img.id}
                                        className={`thumbnail ${idx === selectedImage ? 'active' : ''}`}
                                        onClick={() => setSelectedImage(idx)}
                                    >
                                        <img src={img.thumbnail || img.src} alt={`${product.name} ${idx + 1}`} />
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Info */}
                    <div className="product-info-detail">
                        <h1>{product.name}</h1>

                        <div className="product-meta">
                            {product.categories?.length > 0 && (
                                <span className="category-tag">{product.categories[0]}</span>
                            )}
                            {product.in_stock ? (
                                <span className="stock-status in-stock">In Stock</span>
                            ) : (
                                <span className="stock-status out-of-stock">Out of Stock</span>
                            )}
                        </div>

                        <div className="price-block">
                            {product.on_sale && product.regular_price && (
                                <span className="original-price">${product.regular_price}</span>
                            )}
                            <span className="current-price">${product.price}</span>
                        </div>

                        <div
                            className="product-description"
                            dangerouslySetInnerHTML={{ __html: product.short_description || product.description }}
                        />

                        <div className="add-to-cart-section">
                            <div className="quantity-selector">
                                <button onClick={() => setQuantity(q => Math.max(1, q - 1))}>−</button>
                                <span>{quantity}</span>
                                <button onClick={() => setQuantity(q => q + 1)}>+</button>
                            </div>
                            <button
                                className="btn btn-primary btn-lg"
                                onClick={handleAddToCart}
                                disabled={!product.in_stock}
                            >
                                {addedToCart ? '✓ Added to Cart!' : 'Add to Cart'}
                            </button>
                        </div>

                        {/* Vendor info placeholder */}
                        {product.vendor_name && (
                            <div className="vendor-info">
                                <span>Sold by:</span> {product.vendor_name}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Social Proof Notifications */}
            <SocialProofNotification />
        </div>
    )
}
